// /tickets/feed Main pane. Chronological stream of ship events +
// ticket updates, filterable by the 4-axis tab (All / Team / Agents /
// Me). Realtime — items appear as they land.

import { SUPABASE_URL, restHeaders } from '../../../shared/functions/supabaseConfig.js'

const _openTicketById = async (root, id, fallback) => {
  if (!root || typeof root.update !== 'function') return
  // Optimistic open with what we have so the modal appears immediately.
  root.update({ selectedTicket: { id, ...(fallback || {}) } })
  try {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/tickets?id=eq.${id}&select=*&limit=1`,
      { headers: restHeaders() }
    )
    if (!res.ok) return
    const rows = await res.json()
    const t = Array.isArray(rows) ? rows[0] : null
    if (!t) return
    root.update({ selectedTicket: {
      ...t,
      columnKey: t.column_key,
      issueType: t.issue_type,
      epicName: t.epic_name,
      assigneeEmail: t.assignee_email,
      reporterEmail: t.reporter_email,
      updatedAt: t.updated_at,
      createdAt: t.created_at
    } })
  } catch (_) {}
}

const KIND_LABEL = {
  shipped:       'shipped',
  picked_up:     'picked up',
  blocked:       'blocked',
  handed_off:    'handed off',
  asked_user:    'asked user',
  heartbeat:     'heartbeat',
  ticket_update: 'updated'
}

const KIND_COLOR = {
  shipped:       'green',
  picked_up:     'cyan',
  blocked:       'red',
  handed_off:    'magenta',
  asked_user:    'yellow',
  heartbeat:     'caption',
  ticket_update: 'caption'
}

export const TicketsFeedMain = {
  extends: 'AppMain',

  Header: { extends: 'TicketsHeader' },

  Body: {
    overflow: 'auto',

    Hero: {
      extends: 'PageHero',
      eyebrow: 'WORKSPACE · ACTIVITY FEED',
      headline: 'Feed',
      lede: (el, s) => {
        const items = s.root.feedItems
        if (items == null) return 'Loading recent activity…'
        const n = items.length
        return n ? `${n} recent events · updates stream live from Supabase realtime.` : 'No recent activity yet.'
      }
    },

    Container: {
      flow: 'y',
      gap: 'A',
      padding: 'C B1 D B1',
      maxWidth: 'L+L',
      width: '100%',
      margin: '0 auto',

      List: {
        flow: 'y',
        gap: 'V',

        childExtends: 'FeedItemRow',
        childrenAs: 'state',
        children: (el, s) => {
          const filter = s.root.filterAssignee || null
          const me = (s.root.me?.email || '').toLowerCase()
          const isAgent = (e) => /-agent@symbols\.app$/.test(e || '')
          const items = s.root.feedItems || []
          return items.filter((x) => {
            const e = (x.actorEmail || '').toLowerCase()
            if (!filter) return true
            if (filter === 'me') return me && e === me
            if (filter === 'agents') return isAgent(e)
            if (filter === 'team') return e && !isAgent(e) && e !== me
            return true
          })
        }
      },

      Empty: {
        hide: (el, s) => {
          const items = s.root.feedItems
          // Hide when items not yet loaded (null/undefined) or when items exist
          if (items == null) return true
          return items.length > 0
        },
        flow: 'y',
        align: 'center center',
        gap: 'A',
        padding: 'D',
        textAlign: 'center',

        Icon: {
          extends: 'Icon',
          icon: 'clock',
          boxSize: 'D',
          color: 'caption',
          opacity: '.4'
        },
        Title: {
          tag: 'h3',
          margin: '0',
          fontSize: 'B',
          fontWeight: '600',
          color: 'title',
          text: 'No activity yet'
        },
        Body: {
          tag: 'p',
          margin: '0',
          fontSize: 'A',
          color: 'caption',
          lineHeight: '1.5',
          text: 'Ship events and ticket updates will stream here in real time.'
        }
      }
    }
  }
}

// Single feed entry — actor · kind · ticket · relative time.
export const FeedItemRow = {
  flow: 'x',
  align: 'center left',
  gap: 'V',
  padding: 'Z A',
  background: 'overlay',
  round: 'V',
  borderWidth: 'V',
  borderStyle: 'solid',
  borderColor: 'line',
  cursor: (el, s) => s.ticketId ? 'pointer' : 'default',
  transition: 'border-color 0.12s ease, transform 0.12s ease',
  ':hover': { borderColor: 'title' },
  onClick: (e, el, s) => {
    if (!s.ticketId) return
    const root = s.root || el.getRoot()
    _openTicketById(root, s.ticketId, { external_id: s.ticketExternalId, title: s.ticketTitle })
  },

  Actor: {
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y1',
    fontWeight: '700',
    color: (el, s) => /-agent@symbols\.app$/.test(s.actorEmail || '') ? 'cyan' : 'title',
    text: (el, s) => {
      const label = s.actorDisplay || (s.actorEmail || '').split('@')[0] || '—'
      const isAgent = /-agent@symbols\.app$/.test(s.actorEmail || '')
      return isAgent ? `🤖 ${label.toUpperCase()}` : label
    }
  },

  Kind: {
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    fontWeight: '600',
    color: (el, s) => KIND_COLOR[s.kind] || 'caption',
    text: (el, s) => KIND_LABEL[s.kind] || s.kind || ''
  },

  Ticket: {
    tag: 'span',
    flex: '1',
    fontSize: 'Y1',
    color: 'title',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => {
      const ref = s.ticketExternalId || (s.ticketId ? `#${s.ticketId}` : '')
      const title = (s.ticketTitle || '').replace(/^B-\d+\s*·\s*/, '')
      return [ref, title].filter(Boolean).join(' · ')
    }
  },

  Sha: {
    hide: (el, s) => !s.commitSha,
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    fontFamily: 'monospace',
    color: 'caption',
    text: (el, s) => (s.commitSha || '').slice(0, 8)
  },

  Time: {
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) => {
      const ts = s.tsISO ? new Date(s.tsISO).getTime() : 0
      if (!ts) return ''
      const diffS = Math.floor((Date.now() - ts) / 1000)
      if (diffS < 60) return `${diffS}s`
      if (diffS < 3600) return `${Math.floor(diffS / 60)}m`
      if (diffS < 86400) return `${Math.floor(diffS / 3600)}h`
      return `${Math.floor(diffS / 86400)}d`
    }
  }
}
